import React, { useState, useMemo, useCallback } from 'react';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Search,
  Inbox,
  Trash2,
  UserPlus,
  AlertTriangle,
  Wrench,
  Copy,
  Check,
  Calendar,
  ChevronRight,
  Building2,
  Home,
  MapPin,
  Car
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog';
import { RecallAssignmentCard, type HighlightState } from './RecallAssignmentCard';
import { DealerGroupHeader } from './DealerGroupHeader';
import {
  CUSTOMER_STOP_LABEL,
  getStopDisplayName,
  getStopLocationKey,
  isCustomerStop
} from '@/lib/manualStops/locationKey';
import { TechnicianAssignment } from '@/lib/supabase/technicianAssignments';
import { getManualStopReasonLabel } from '@/lib/validation/manualStopValidation';
import { cn } from '@/lib/utils';
import { useToast } from '@/hooks/use-toast';
import { CustomerContactReveal } from '@/components/CustomerContactReveal';

// Stop type filter for tabs
export type StopTypeFilter = 'all_stops' | 'recalls' | 'manual';

export interface SelectedRecall {
  vin: string;
  campaign_number: string;
  recall_title?: string;
  dealer_name?: string;
  dealer_address?: string;
  dealer_city?: string;
  dealer_region?: string;
  dealer_postal_code?: string;
  dealer_latitude?: number;
  dealer_longitude?: number;
  dealer_phone?: string;
  year?: string | number;
  make?: string;
  model?: string;
  stock_number?: string;
  // Manual stop support
  is_manual_stop?: boolean;
  manual_stop_id?: string;
  manual_stop_reason?: string;
  manual_stop_notes?: string;
  // Customer-stop columns (selected_recalls)
  stop_kind?: 'business' | 'customer';
  contact_id?: string | null;
  dispatcher_note?: string | null;
  address_line2?: string | null;
}

interface RecallAssignmentListProps {
  recalls: SelectedRecall[];
  assignments: TechnicianAssignment[];
  selectedRecallKeys: Set<string>;
  onSelectionChange: (keys: Set<string>) => void;
  onUnassign?: (assignmentId: string) => void;
  onRemoveRecall?: (vin: string, campaignNumber: string) => void;
  filterMode: 'all' | 'assigned' | 'unassigned';
  selectedDate: Date; // The date for filtering assigned recalls
  loading?: boolean;
  className?: string;
  onBulkAssign?: () => void;
  onBulkUnassign?: (assignmentIds: string[]) => void;
  onClearAllFromTech?: () => void;
  onClearSelectedUnassigned?: () => void;
  isAssigning?: boolean;
  technicianName?: string | null;
  allowReassignment?: boolean;
  selectedTechnicianId?: string | null;
  techAssignedCount?: number;
  startLocationDirty?: boolean; // True if start location changed from saved route state
}

// Helper to create unique key for a recall
const createRecallKey = (vin: string, campaignNumber: string): string => {
  return `${vin}|${campaignNumber}`;
};

/** Street, City, ST ZIP — used for manual stop rows (assignments exclude manual from assignmentMap). */
/** "500 Oak St" + "Apt 4B" → "500 Oak St, Apt 4B" (the unit is stored separately). */
function withUnit(street: string | undefined, unit: string | null | undefined): string | undefined {
  if (!street) return street;
  return unit ? `${street}, ${unit}` : street;
}

function formatManualStopFullAddress(parts: {
  dealer_address?: string | null | undefined;
  dealer_city?: string | null | undefined;
  dealer_region?: string | null | undefined;
  dealer_postal_code?: string | null | undefined;
}): string {
  const street = (parts.dealer_address ?? '').trim();
  const city = (parts.dealer_city ?? '').trim();
  const region = (parts.dealer_region ?? '').trim();
  const zip = (parts.dealer_postal_code ?? '').trim();
  const cityStateZip = [city, [region, zip].filter(Boolean).join(' ')].filter(Boolean).join(', ');
  if (street && cityStateZip) {
    return `${street}, ${cityStateZip}`;
  }
  if (cityStateZip) {
    return cityStateZip;
  }
  return street || '—';
}

interface DealerGroup {
  /** Grouping identity (getStopLocationKey for customer stops, else the dealer name) */
  groupKey: string;
  dealerName: string;
  isCustomer?: boolean;
  dealerAddress?: string;
  dealerCity?: string;
  dealerRegion?: string;
  dealerPostalCode?: string;
  recalls: SelectedRecall[];
}

interface ManualStopLocationGroup {
  /** getStopLocationKey — customer stops are all named 'Customer stop', so never key by name */
  key: string;
  locationName: string;
  isCustomer: boolean;
  locationAddress?: string | undefined;
  locationCity?: string | undefined;
  locationRegion?: string | undefined;
  locationPostalCode?: string | undefined;
  stops: TechnicianAssignment[];
}

function manualStopRowShellClass(stopHighlight: HighlightState, stopIsSelected: boolean): string {
  return cn(
    'mx-4 my-2 p-3 rounded-lg border',
    'flex flex-row gap-3 transition-all duration-200 items-start',
    stopHighlight === 'blue' && 'ring-2 ring-blue-500 bg-blue-500/10',
    stopHighlight === 'blue' && 'dark:bg-blue-500/20 border-blue-500',
    stopHighlight === 'red' && 'ring-2 ring-red-500 bg-red-500/10',
    stopHighlight === 'red' && 'dark:bg-red-500/20 border-red-500',
    stopHighlight === 'none' && 'bg-card border-accent/25 dark:border-accent/35',
    stopIsSelected && 'bg-primary/5 border-primary/30 dark:bg-primary/10'
  );
}

function manualStopTypeBadgeClass(stopHighlight: HighlightState): string {
  return cn(
    'text-xs shrink-0',
    stopHighlight === 'blue' &&
      'bg-blue-100 text-blue-700 border-blue-300 dark:bg-blue-900/30 dark:text-blue-400',
    stopHighlight === 'red' &&
      'bg-red-100 text-red-700 border-red-300 dark:bg-red-900/30 dark:text-red-400',
    stopHighlight === 'none' &&
      'bg-accent/15 text-accent border border-accent/40 dark:bg-accent/20 dark:text-accent'
  );
}

/**
 * Recall assignment list with dealer grouping, search, and selection
 * Implements progressive filtering - only shows unassigned recalls by default
 */
/* eslint-disable max-lines-per-function, complexity -- single scrollable list; split is a larger refactor */
export const RecallAssignmentList: React.FC<RecallAssignmentListProps> = ({
  recalls,
  assignments,
  selectedRecallKeys,
  onSelectionChange,
  onUnassign,
  onRemoveRecall,
  filterMode,
  selectedDate, // Used for date comparison to detect rescheduling
  loading = false,
  className = '',
  onBulkAssign,
  onBulkUnassign,
  onClearAllFromTech,
  onClearSelectedUnassigned,
  isAssigning = false,
  technicianName = null,
  allowReassignment = true,
  selectedTechnicianId = null,
  techAssignedCount = 0,
  startLocationDirty = false
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedDealers, setExpandedDealers] = useState<Set<string>>(new Set());
  const [expandedManualStopGroups, setExpandedManualStopGroups] = useState<Set<string>>(new Set());
  const [showClearConfirmDialog, setShowClearConfirmDialog] = useState(false);
  const [showClearUnassignedDialog, setShowClearUnassignedDialog] = useState(false);
  // Stop type filter (all, recalls only, manual only)
  const [stopTypeFilter, setStopTypeFilter] = useState<StopTypeFilter>('all_stops');
  // State for VIN copy feedback
  const [copiedVin, setCopiedVin] = useState<string | null>(null);
  const { toast } = useToast();

  // Helper to copy VIN to clipboard
  const handleCopyVin = useCallback(
    async (vin: string, e: React.MouseEvent) => {
      e.stopPropagation();
      try {
        await navigator.clipboard.writeText(vin);
        setCopiedVin(vin);
        toast({
          title: 'Success',
          description: 'VIN copied to clipboard!',
          variant: 'success'
        });
        setTimeout(() => setCopiedVin(null), 2000);
      } catch {
        toast({
          title: 'Error',
          description: 'Failed to copy VIN to clipboard',
          variant: 'destructive'
        });
      }
    },
    [toast]
  );

  // Helper to format assignment date for display
  const formatAssignmentDate = useCallback((dateString: string | undefined): string | null => {
    if (!dateString) return null;
    try {
      const match = dateString.match(/^(\d{4}-\d{2}-\d{2})/);
      if (match && match[1]) {
        const parts = match[1].split('-').map(Number);
        const year = parts[0] ?? 2026;
        const month = parts[1] ?? 1;
        const day = parts[2] ?? 1;
        const date = new Date(year, month - 1, day, 12, 0, 0);
        return date.toLocaleDateString('en-US', {
          month: 'short',
          day: 'numeric',
          year: 'numeric'
        });
      }
    } catch {
      // ignore
    }
    return null;
  }, []);

  // Helper to format name (e.g., "ken.tufekci" -> "Ken Tufekci")
  const formatDisplayName = useCallback((name: string | undefined | null): string | null => {
    if (!name) return null;
    return name
      .replace(/[._]/g, ' ') // Replace dots and underscores with spaces
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  }, []);

  // Create a map of assigned recalls for quick lookup
  const assignmentMap = useMemo(() => {
    const map = new Map<string, TechnicianAssignment>();
    // Group assignments by technician for debugging
    const byTech = new Map<string, number>();

    assignments.forEach(assignment => {
      const key = createRecallKey(assignment.vin, assignment.campaign_number);
      // Track non-completed/cancelled assignments (including manual stops)
      if (
        assignment.assignment_status !== 'completed' &&
        assignment.assignment_status !== 'cancelled'
      ) {
        map.set(key, assignment);
        // Count by technician
        const techName = assignment.technician_name || 'Unknown';
        byTech.set(techName, (byTech.get(techName) || 0) + 1);
      }
    });

    console.log('[RecallAssignmentList] Assignment map by technician:', Object.fromEntries(byTech));

    // Debug: Show all assignments for Demo Tech 3 specifically
    const demoTech3Assignments = Array.from(map.values()).filter(
      a => a.technician_name === 'Demo Tech 3'
    );
    if (demoTech3Assignments.length > 0) {
      // Show unique dealer names from assignments
      const uniqueAssignmentDealers = [...new Set(demoTech3Assignments.map(a => a.dealer_name))];
      console.log(
        '[RecallAssignmentList] Demo Tech 3 assignments - unique dealers from assignments:',
        uniqueAssignmentDealers
      );
      console.log(
        '[RecallAssignmentList] Demo Tech 3 assignments:',
        demoTech3Assignments.map(a => `${a.vin.slice(-6)}|${a.campaign_number}|${a.dealer_name}`)
      );
    }

    return map;
  }, [assignments]);

  // Count selected unassigned recalls (those without assignments)
  const selectedUnassignedCount = useMemo(() => {
    let count = 0;
    selectedRecallKeys.forEach(key => {
      if (!assignmentMap.has(key)) {
        count++;
      }
    });
    return count;
  }, [selectedRecallKeys, assignmentMap]);

  // Get manual stops for the selected technician
  const manualStops = useMemo(() => {
    return assignments.filter(
      a =>
        a.stop_type === 'manual' &&
        a.technician_id === selectedTechnicianId &&
        a.assignment_status !== 'completed' &&
        a.assignment_status !== 'cancelled'
    );
  }, [assignments, selectedTechnicianId]);

  // Get manual stops for OTHER technicians (for "all" mode display)
  const manualStopsByOtherTech = useMemo(() => {
    const byTech = new Map<string, TechnicianAssignment[]>();
    assignments
      .filter(
        a =>
          a.stop_type === 'manual' &&
          a.technician_id !== selectedTechnicianId &&
          a.assignment_status !== 'completed' &&
          a.assignment_status !== 'cancelled'
      )
      .forEach(stop => {
        const techName = stop.technician_name || 'Unknown Technician';
        if (!byTech.has(techName)) {
          byTech.set(techName, []);
        }
        byTech.get(techName)!.push(stop);
      });
    return byTech;
  }, [assignments, selectedTechnicianId]);

  // Group manual stops by location for other techs
  const manualStopGroupsByOtherTech = useMemo(() => {
    const result = new Map<string, ManualStopLocationGroup[]>();
    manualStopsByOtherTech.forEach((stops, techName) => {
      const groups = new Map<string, ManualStopLocationGroup>();
      stops.forEach(stop => {
        const locationKey = getStopLocationKey(stop);
        if (!groups.has(locationKey)) {
          groups.set(locationKey, {
            key: locationKey,
            locationName: getStopDisplayName(stop),
            isCustomer: isCustomerStop(stop),
            locationAddress: withUnit(stop.dealer_address, stop.address_line2),
            locationCity: stop.dealer_city,
            locationRegion: stop.dealer_region,
            locationPostalCode: stop.dealer_postal_code,
            stops: []
          });
        }
        groups.get(locationKey)!.stops.push(stop);
      });
      result.set(techName, Array.from(groups.values()));
    });
    return result;
  }, [manualStopsByOtherTech]);

  // Create a set of VIN+campaign keys from manual stops (for filtering out recalls covered by manual stops)
  const manualStopKeys = useMemo(() => {
    const keys = new Set<string>();
    manualStops.forEach(stop => {
      if (stop.vin) {
        keys.add(createRecallKey(stop.vin, stop.campaign_number || ''));
      }
    });
    return keys;
  }, [manualStops]);

  // Group manual stops by location name for the All Stops view
  const manualStopGroups = useMemo((): ManualStopLocationGroup[] => {
    const groups = new Map<string, ManualStopLocationGroup>();

    manualStops.forEach(stop => {
      const locationKey = getStopLocationKey(stop);

      if (!groups.has(locationKey)) {
        groups.set(locationKey, {
          key: locationKey,
          locationName: getStopDisplayName(stop),
          isCustomer: isCustomerStop(stop),
          locationAddress: withUnit(stop.dealer_address, stop.address_line2),
          locationCity: stop.dealer_city,
          locationRegion: stop.dealer_region,
          locationPostalCode: stop.dealer_postal_code,
          stops: []
        });
      }

      const group = groups.get(locationKey);
      if (group) group.stops.push(stop);
    });

    // Sort groups by location name (then address, so customer stops have a stable order)
    return Array.from(groups.values()).sort(
      (a, b) =>
        a.locationName.localeCompare(b.locationName) ||
        (a.locationAddress ?? '').localeCompare(b.locationAddress ?? '')
    );
  }, [manualStops]);

  // Filter recalls based on filter mode and search term
  // Now includes both recalls and staged manual stops together
  const filteredRecalls = useMemo(() => {
    let filtered = recalls;

    // Apply filter mode
    if (filterMode === 'assigned') {
      // Show all recalls assigned to the selected technician (any date)
      // The date picker is for NEW assignments, not for filtering the view
      filtered = filtered.filter(recall => {
        const key = createRecallKey(recall.vin, recall.campaign_number);
        const assignment = assignmentMap.get(key);
        return assignment && assignment.technician_id === selectedTechnicianId;
      });
    } else if (filterMode === 'unassigned') {
      filtered = filtered.filter(recall => {
        const key = createRecallKey(recall.vin, recall.campaign_number);
        // Not in assignments AND not covered by a manual stop (from assignments)
        return !assignmentMap.has(key) && !manualStopKeys.has(key);
      });
    }
    // 'all' mode: no filtering, show everything (recalls + staged manual stops)

    // Apply stop type filter
    if (stopTypeFilter === 'recalls') {
      filtered = filtered.filter(recall => !recall.is_manual_stop);
    } else if (stopTypeFilter === 'manual') {
      filtered = filtered.filter(recall => recall.is_manual_stop);
    }
    // 'all_stops': no stop type filtering

    // Apply search filter
    if (searchTerm.trim()) {
      const search = searchTerm.toLowerCase();
      filtered = filtered.filter(recall => {
        return (
          recall.vin.toLowerCase().includes(search) ||
          recall.campaign_number.toLowerCase().includes(search) ||
          recall.dealer_name?.toLowerCase().includes(search) ||
          recall.make?.toLowerCase().includes(search) ||
          recall.model?.toLowerCase().includes(search) ||
          recall.recall_title?.toLowerCase().includes(search) ||
          recall.manual_stop_reason?.toLowerCase().includes(search)
        );
      });
    }

    return filtered;
  }, [
    recalls,
    assignmentMap,
    manualStopKeys,
    filterMode,
    selectedTechnicianId,
    stopTypeFilter,
    searchTerm
  ]);

  // Compute counts for display-only totals
  const recallCount = useMemo(() => {
    return recalls.filter(r => !r.is_manual_stop).length;
  }, [recalls]);

  const stagedManualStopCount = useMemo(() => {
    return recalls.filter(r => r.is_manual_stop).length;
  }, [recalls]);

  // Group recalls by dealer (includes staged manual stops for UNASSIGNED section)
  // IMPORTANT: For assigned recalls, use dealer_name from the assignment (technician_assignments table)
  // rather than from selected_recalls, because assignment dealer_name is more accurate/current
  const dealerGroups = useMemo((): DealerGroup[] => {
    const groups = new Map<string, DealerGroup>();

    // Include all items (recalls + staged manual stops) - they'll be split by type only in ASSIGNED sections
    filteredRecalls.forEach(recall => {
      // Check if this recall is assigned - if so, use assignment's dealer_name
      const recallKey = createRecallKey(recall.vin, recall.campaign_number);
      const assignment = assignmentMap.get(recallKey);

      // Customer stops group by household; everything else by dealer name, preferring the
      // assignment's dealer_name (more current than selected_recalls).
      const location = {
        stop_kind: recall.stop_kind ?? assignment?.stop_kind,
        contact_id: recall.contact_id ?? assignment?.contact_id,
        dealer_address: recall.dealer_address,
        address_line2: recall.address_line2 ?? assignment?.address_line2,
        dealer_postal_code: recall.dealer_postal_code
      };
      const isCustomer = isCustomerStop(location);
      const dealerName = assignment?.dealer_name || recall.dealer_name || 'Unknown Dealer';
      const dealerKey = isCustomer ? getStopLocationKey(location) : dealerName;

      if (!groups.has(dealerKey)) {
        const address = withUnit(recall.dealer_address, location.address_line2);
        groups.set(dealerKey, {
          groupKey: dealerKey,
          dealerName: isCustomer ? CUSTOMER_STOP_LABEL : dealerName,
          isCustomer,
          ...(address !== undefined && { dealerAddress: address }),
          ...(recall.dealer_city !== undefined && { dealerCity: recall.dealer_city }),
          ...(recall.dealer_region !== undefined && { dealerRegion: recall.dealer_region }),
          ...(recall.dealer_postal_code !== undefined && {
            dealerPostalCode: recall.dealer_postal_code
          }),
          recalls: []
        });
      }

      const group = groups.get(dealerKey);
      if (group) group.recalls.push(recall);
    });

    // Sort groups by dealer name (then address, so customer stops have a stable order)
    return Array.from(groups.values()).sort(
      (a, b) =>
        a.dealerName.localeCompare(b.dealerName) ||
        (a.dealerAddress ?? '').localeCompare(b.dealerAddress ?? '')
    );
  }, [filteredRecalls, assignmentMap]);

  // In "all" mode, split dealer groups into three sections:
  // 1. Assigned to selected technician
  // 2. Assigned to other technicians (grouped by tech name)
  // 3. Truly unassigned
  const { assignedToSelectedGroups, assignedToOthersMap, trulyUnassignedGroups } = useMemo(() => {
    console.log('[RecallAssignmentList] Processing groupings:', {
      filterMode,
      recallsCount: recalls.length,
      assignmentsCount: assignments.length,
      assignmentMapSize: assignmentMap.size,
      dealerGroupsCount: dealerGroups.length,
      selectedTechnicianId
    });

    // Debug: Show dealers in recalls array for Demo Tech 3's VINs
    const demoTech3Vins = new Set(
      Array.from(assignmentMap.values())
        .filter(a => a.technician_name === 'Demo Tech 3')
        .map(a => a.vin)
    );
    const recallsForDemoTech3 = recalls.filter(r => demoTech3Vins.has(r.vin));
    if (recallsForDemoTech3.length > 0) {
      // Show unique dealers
      const uniqueDealers = [...new Set(recallsForDemoTech3.map(r => r.dealer_name))];
      console.log('[RecallAssignmentList] Demo Tech 3 recalls - unique dealers:', uniqueDealers);
      console.log(
        '[RecallAssignmentList] Recalls in recalls array for Demo Tech 3 VINs:',
        recallsForDemoTech3.map(r => `${r.vin.slice(-6)}|${r.campaign_number}|${r.dealer_name}`)
      );
    } else if (demoTech3Vins.size > 0) {
      console.log(
        '[RecallAssignmentList] WARNING: Demo Tech 3 has',
        demoTech3Vins.size,
        'VINs but none found in recalls array. VINs:',
        Array.from(demoTech3Vins)
      );
    }

    if (filterMode !== 'all') {
      return {
        assignedToSelectedGroups: dealerGroups,
        assignedToOthersMap: new Map<string, DealerGroup[]>(),
        trulyUnassignedGroups: [] as DealerGroup[]
      };
    }

    const assignedToSelected: DealerGroup[] = [];
    // Map of technician name -> dealer groups assigned to that tech
    const othersMap = new Map<string, DealerGroup[]>();
    const trulyUnassigned: DealerGroup[] = [];

    // Debug: Track matching
    let matchedToSelected = 0;
    let matchedToOthers = 0;
    let unmatched = 0;

    dealerGroups.forEach(group => {
      // Categorize each recall in the group
      const recallsAssignedToSelected: SelectedRecall[] = [];
      const recallsByOtherTech = new Map<string, SelectedRecall[]>();
      const unassignedRecalls: SelectedRecall[] = [];

      group.recalls.forEach(recall => {
        const key = createRecallKey(recall.vin, recall.campaign_number);
        const assignment = assignmentMap.get(key);

        if (assignment) {
          if (assignment.technician_id === selectedTechnicianId) {
            // Exclude manual stops - they're rendered via manualStopGroups
            if (!recall.is_manual_stop) {
              recallsAssignedToSelected.push(recall);
              matchedToSelected++;
            }
          } else {
            // Assigned to another technician
            // Exclude manual stops - they're rendered via manualStopGroupsByOtherTech
            if (!recall.is_manual_stop) {
              const techName = assignment.technician_name || 'Unknown Technician';
              if (!recallsByOtherTech.has(techName)) {
                recallsByOtherTech.set(techName, []);
              }
              recallsByOtherTech.get(techName)!.push(recall);
              matchedToOthers++;
            }
          }
        } else if (!manualStopKeys.has(key)) {
          // Only add to unassigned if not covered by a manual stop
          unassignedRecalls.push(recall);
          unmatched++;
        }
      });

      // Create dealer group for recalls assigned to selected tech
      if (recallsAssignedToSelected.length > 0) {
        assignedToSelected.push({
          ...group,
          recalls: recallsAssignedToSelected
        });
      }

      // Create dealer groups for recalls assigned to other techs
      recallsByOtherTech.forEach((recalls, techName) => {
        if (!othersMap.has(techName)) {
          othersMap.set(techName, []);
        }
        othersMap.get(techName)!.push({
          ...group,
          recalls
        });
      });

      // Create dealer group for truly unassigned recalls
      if (unassignedRecalls.length > 0) {
        trulyUnassigned.push({
          ...group,
          recalls: unassignedRecalls
        });
      }
    });

    console.log('[RecallAssignmentList] Grouping results:', {
      matchedToSelected,
      matchedToOthers,
      unmatched,
      assignedToSelectedDealers: assignedToSelected.length,
      otherTechs: Array.from(othersMap.keys()),
      trulyUnassignedDealers: trulyUnassigned.length
    });

    return {
      assignedToSelectedGroups: assignedToSelected,
      assignedToOthersMap: othersMap,
      trulyUnassignedGroups: trulyUnassigned
    };
  }, [dealerGroups, filterMode, assignmentMap, manualStopKeys, selectedTechnicianId]);

  // Initialize expanded dealers (collapsed by default)
  // Don't auto-expand - let users expand as needed

  // Count selected recalls that ARE assigned to the selected technician (for bulk unassign)
  const selectedAssignedToTechCount = useMemo(() => {
    let count = 0;
    selectedRecallKeys.forEach(key => {
      const assignment = assignmentMap.get(key);
      if (assignment && assignment.technician_id === selectedTechnicianId) {
        count++;
      }
    });
    return count;
  }, [selectedRecallKeys, assignmentMap, selectedTechnicianId]);

  // Helper: format selectedDate as YYYY-MM-DD for comparison with assignment dates
  const selectedDateStr = useMemo(() => {
    const y = selectedDate.getFullYear();
    const m = String(selectedDate.getMonth() + 1).padStart(2, '0');
    const d = String(selectedDate.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  }, [selectedDate]);

  // Today's date as YYYY-MM-DD for overdue comparison
  const todayStr = useMemo(() => {
    const now = new Date();
    const y = now.getFullYear();
    const m = String(now.getMonth() + 1).padStart(2, '0');
    const d = String(now.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  }, []);

  // Determine highlight state for a recall or manual stop based on its assignment date
  // Red = overdue (before today), Blue = matches selected date, None = future/other
  const getHighlightState = useCallback(
    (assignment: TechnicianAssignment | undefined): HighlightState => {
      if (!assignment?.expected_visit_date) return 'none';
      if (selectedTechnicianId && assignment.technician_id !== selectedTechnicianId) return 'none';

      const match = assignment.expected_visit_date.match(/^(\d{4}-\d{2}-\d{2})/);
      const dateStr = match?.[1];
      if (!dateStr) return 'none';

      if (dateStr < todayStr) return 'red';
      if (dateStr === selectedDateStr) return 'blue';
      return 'none';
    },
    [selectedTechnicianId, todayStr, selectedDateStr]
  );

  // Count selected recalls that are NEW assignments (no existing assignment)
  const selectedNewAssignCount = useMemo(() => {
    let count = 0;
    selectedRecallKeys.forEach(key => {
      const assignment = assignmentMap.get(key);
      if (!assignment) count++;
    });
    return count;
  }, [selectedRecallKeys, assignmentMap]);

  // Count selected recalls that are REASSIGNMENTS (different tech or same tech different date)
  const selectedReassignCount = useMemo(() => {
    let count = 0;
    selectedRecallKeys.forEach(key => {
      const assignment = assignmentMap.get(key);
      if (!assignment) return; // new assignment, not a reassignment
      // Different technician → reassign
      if (assignment.technician_id !== selectedTechnicianId) {
        count++;
        return;
      }
      // Same technician, different date → reassign (reschedule)
      if (assignment.expected_visit_date) {
        const dateMatch = assignment.expected_visit_date.match(/^(\d{4}-\d{2}-\d{2})/);
        const assignmentDateStr = dateMatch ? dateMatch[1] : null;
        if (assignmentDateStr !== selectedDateStr) {
          count++;
        }
      }
    });
    return count;
  }, [selectedRecallKeys, assignmentMap, selectedTechnicianId, selectedDateStr]);

  // Total actionable count (used for button disabled state)
  const selectedAssignableCount = selectedNewAssignCount + selectedReassignCount;

  // Get assignment IDs for selected recalls assigned to current tech (for bulk unassign)
  const getSelectedAssignmentIds = useCallback((): string[] => {
    const ids: string[] = [];
    selectedRecallKeys.forEach(key => {
      const assignment = assignmentMap.get(key);
      if (assignment && assignment.technician_id === selectedTechnicianId) {
        ids.push(assignment.id);
      }
    });
    return ids;
  }, [selectedRecallKeys, assignmentMap, selectedTechnicianId]);

  // totalCount could be used for future features
  // const totalCount = recalls.length;

  // Handle individual recall selection
  const handleRecallSelect = useCallback(
    (vin: string, campaignNumber: string) => {
      const key = createRecallKey(vin, campaignNumber);
      const newSelection = new Set(selectedRecallKeys);
      const isAssigned = assignmentMap.has(key);

      // Allow selection of assigned recalls only if reassignment is enabled
      if (!isAssigned || allowReassignment) {
        if (newSelection.has(key)) {
          newSelection.delete(key);
        } else {
          newSelection.add(key);
        }
        onSelectionChange(newSelection);
      }
    },
    [selectedRecallKeys, onSelectionChange, assignmentMap, allowReassignment]
  );

  // Handle select all unassigned
  const handleSelectAllUnassigned = useCallback(() => {
    const newSelection = new Set(selectedRecallKeys);
    // Get unassigned keys
    const unassignedKeys = filteredRecalls
      .filter(recall => {
        const key = createRecallKey(recall.vin, recall.campaign_number);
        return !assignmentMap.has(key) && !manualStopKeys.has(key);
      })
      .map(recall => createRecallKey(recall.vin, recall.campaign_number));

    // Toggle: if all unassigned are selected, deselect them; otherwise select all
    const allSelected =
      unassignedKeys.length > 0 && unassignedKeys.every(key => selectedRecallKeys.has(key));

    if (allSelected) {
      unassignedKeys.forEach(key => newSelection.delete(key));
    } else {
      unassignedKeys.forEach(key => newSelection.add(key));
    }
    onSelectionChange(newSelection);
  }, [filteredRecalls, assignmentMap, manualStopKeys, selectedRecallKeys, onSelectionChange]);

  // Handle select all assigned (ALL technicians, including manual stops)
  const handleSelectAllAssigned = useCallback(() => {
    const newSelection = new Set(selectedRecallKeys);

    // Get ALL assigned recall keys (any technician)
    const assignedRecallKeys = filteredRecalls
      .filter(recall => {
        const key = createRecallKey(recall.vin, recall.campaign_number);
        return assignmentMap.has(key);
      })
      .map(recall => createRecallKey(recall.vin, recall.campaign_number));

    // Get ALL manual stop keys (selected technician)
    const manualStopKeysList = manualStops
      .filter(stop => stop.vin)
      .map(stop => createRecallKey(stop.vin!, stop.campaign_number || ''));

    // Get ALL manual stop keys (other technicians)
    const otherTechManualStopKeys: string[] = [];
    manualStopsByOtherTech.forEach(stops => {
      stops.forEach(stop => {
        if (stop.vin) {
          otherTechManualStopKeys.push(createRecallKey(stop.vin, stop.campaign_number || ''));
        }
      });
    });

    // Combine all assigned keys
    const allAssignedKeys = [
      ...assignedRecallKeys,
      ...manualStopKeysList,
      ...otherTechManualStopKeys
    ];

    // Toggle: if all assigned are selected, deselect them; otherwise select all
    const allSelected =
      allAssignedKeys.length > 0 && allAssignedKeys.every(key => selectedRecallKeys.has(key));

    if (allSelected) {
      allAssignedKeys.forEach(key => newSelection.delete(key));
    } else {
      allAssignedKeys.forEach(key => newSelection.add(key));
    }
    onSelectionChange(newSelection);
  }, [
    filteredRecalls,
    assignmentMap,
    manualStops,
    manualStopsByOtherTech,
    selectedRecallKeys,
    onSelectionChange
  ]);

  // Handle dealer group select all
  // Takes the actual recalls array to operate on, not just dealer name
  // This ensures we only select/deselect recalls that are in the specific section
  const handleDealerSelectAll = useCallback(
    (recallsToToggle: SelectedRecall[], selected: boolean) => {
      const newSelection = new Set(selectedRecallKeys);

      recallsToToggle.forEach(recall => {
        const key = createRecallKey(recall.vin, recall.campaign_number);
        // Only toggle unassigned recalls or if reassignment is allowed
        if (!assignmentMap.has(key) || allowReassignment) {
          if (selected) {
            newSelection.add(key);
          } else {
            newSelection.delete(key);
          }
        }
      });

      onSelectionChange(newSelection);
    },
    [selectedRecallKeys, assignmentMap, onSelectionChange, allowReassignment]
  );

  // Toggle dealer expansion
  const toggleDealerExpand = useCallback((dealerName: string) => {
    setExpandedDealers(prev => {
      const newSet = new Set(prev);
      if (newSet.has(dealerName)) {
        newSet.delete(dealerName);
      } else {
        newSet.add(dealerName);
      }
      return newSet;
    });
  }, []);

  // Toggle manual stop group expansion
  const toggleManualStopGroupExpand = useCallback((locationName: string) => {
    setExpandedManualStopGroups(prev => {
      const newSet = new Set(prev);
      if (newSet.has(locationName)) {
        newSet.delete(locationName);
      } else {
        newSet.add(locationName);
      }
      return newSet;
    });
  }, []);

  /** Manual stop row — nested under location groups for assigned manual stops */
  const renderManualStopRow = (stop: TechnicianAssignment): React.ReactElement => {
    const stopHighlight = getHighlightState(stop);
    const scheduledDateLabel = formatAssignmentDate(stop.expected_visit_date);
    const manualStopKey = createRecallKey(stop.vin, stop.campaign_number);
    const isManualStopSelected = selectedRecallKeys.has(manualStopKey);

    const handleManualStopCheckboxChange = () => {
      const newKeys = new Set(selectedRecallKeys);
      if (isManualStopSelected) {
        newKeys.delete(manualStopKey);
      } else {
        newKeys.add(manualStopKey);
      }
      onSelectionChange(newKeys);
    };

    return (
      <div key={stop.id} className={manualStopRowShellClass(stopHighlight, false)}>
        {/* Checkbox - matches RecallAssignmentCard */}
        <div className='pt-0.5 flex-shrink-0'>
          <Checkbox
            checked={isManualStopSelected}
            onCheckedChange={handleManualStopCheckboxChange}
            className={cn(
              'transition-all border-2 w-5 h-5',
              isManualStopSelected && 'border-primary bg-primary text-white',
              'data-[state=checked]:bg-primary data-[state=checked]:border-primary data-[state=checked]:text-white'
            )}
            style={{
              backgroundColor: isManualStopSelected ? 'hsl(var(--primary))' : undefined,
              borderColor: isManualStopSelected ? 'hsl(var(--primary))' : undefined,
              color: isManualStopSelected ? 'white' : undefined
            }}
          />
        </div>

        {/* Vehicle & Stop Info */}
        <div className='flex-1 min-w-0 space-y-1'>
          {stop.vehicle_year && stop.vehicle_make && stop.vehicle_model && (
            <div className='flex items-center gap-2'>
              <Car className='h-4 w-4 flex-shrink-0 text-muted-foreground' />
              <span className='font-semibold text-sm text-foreground truncate'>
                {stop.vehicle_year} {stop.vehicle_make} {stop.vehicle_model}
              </span>
            </div>
          )}

          <div className='flex flex-col gap-0.5'>
            {stop.vin && (
              <div className='flex items-center gap-1.5 min-w-0'>
                <code
                  className={cn(
                    'font-mono text-xs px-1.5 py-0.5 rounded border truncate',
                    'bg-slate-100 dark:bg-slate-800 border-slate-300 dark:border-slate-600',
                    'text-slate-700 dark:text-slate-200'
                  )}
                >
                  {stop.vin}
                </code>
                <button
                  type='button'
                  onClick={e => handleCopyVin(stop.vin, e)}
                  title='Copy VIN to clipboard'
                  className='inline-flex items-center justify-center h-5 w-5 rounded hover:bg-muted'
                >
                  {copiedVin === stop.vin ? (
                    <Check className='h-3 w-3 text-green-600' />
                  ) : (
                    <Copy className='h-3 w-3 text-slate-500' />
                  )}
                </button>
              </div>
            )}

            <div className='flex items-center gap-1.5 min-w-0'>
              <Badge variant='outline' className={manualStopTypeBadgeClass(stopHighlight)}>
                Manual
              </Badge>
              <span className='text-xs text-slate-600 dark:text-slate-300 truncate'>
                {getManualStopReasonLabel(stop.manual_stop_reason || '') || 'Manual Stop'}
              </span>
            </div>

            {stop.campaign_number &&
              stop.campaign_number !== 'MANUAL' &&
              !stop.campaign_number.startsWith('MANUAL-') && (
                <span className='text-xs text-muted-foreground'>
                  Campaign: {stop.campaign_number}
                </span>
              )}

            {stop.notes && (
              <span className='text-xs text-muted-foreground italic truncate'>{stop.notes}</span>
            )}
            {stop.dispatcher_note && (
              <span className='text-xs text-muted-foreground truncate' title={stop.dispatcher_note}>
                <span className='font-medium text-foreground'>Note for technician: </span>
                {stop.dispatcher_note}
              </span>
            )}
          </div>
        </div>

        <div className='flex flex-col items-end gap-1.5 flex-shrink-0 ml-2'>
          <div className='flex flex-row items-center justify-end gap-1.5'>
            {onUnassign && (
              <button
                type='button'
                onClick={() => onUnassign(stop.id)}
                className={cn(
                  'flex items-center justify-center h-7 w-7 rounded-full',
                  'bg-red-100 dark:bg-red-900/50 text-red-700 dark:text-red-300',
                  'hover:bg-red-200 dark:hover:bg-red-900/70 transition-colors',
                  'border border-red-300 dark:border-red-700'
                )}
                title='Remove from route'
              >
                <Trash2 className='h-3.5 w-3.5' />
              </button>
            )}
          </div>

          {stop.technician_name && (
            <Badge
              className={cn(
                'text-xs bg-purple-50 text-purple-700',
                'dark:bg-purple-900/30 dark:text-purple-400',
                'border border-purple-300 dark:border-purple-600'
              )}
            >
              {stop.technician_name}
            </Badge>
          )}

          {scheduledDateLabel && (
            <div
              className={cn(
                'flex items-center gap-1 text-xs',
                'text-slate-700 dark:text-slate-200'
              )}
            >
              <Calendar className='h-3 w-3' />
              <span>{scheduledDateLabel}</span>
            </div>
          )}

          {stop.assigned_by_name && (
            <span className='text-xs text-slate-600 dark:text-slate-300'>
              by {formatDisplayName(stop.assigned_by_name)}
            </span>
          )}
        </div>
      </div>
    );
  };

  /** Collapsible location group for manual stops — shared by All Stops and Manual tab */
  const renderManualStopLocationGroup = (group: ManualStopLocationGroup, groupKey: string) => {
    const isExpanded = expandedManualStopGroups.has(groupKey);
    const groupAddressLine = formatManualStopFullAddress({
      dealer_address: group.locationAddress,
      dealer_city: group.locationCity,
      dealer_region: group.locationRegion,
      dealer_postal_code: group.locationPostalCode
    });
    const groupHighlight = (() => {
      let hasBlue = false;
      for (const stop of group.stops) {
        const state = getHighlightState(stop);
        if (state === 'red') return 'red';
        if (state === 'blue') hasBlue = true;
      }
      return hasBlue ? 'blue' : 'none';
    })();

    // Selection state for all stops in this group
    const groupStopKeys = group.stops.map(stop => createRecallKey(stop.vin, stop.campaign_number));
    const selectedCount = groupStopKeys.filter(key => selectedRecallKeys.has(key)).length;
    const allSelected = selectedCount === group.stops.length && group.stops.length > 0;
    const someSelected = selectedCount > 0 && !allSelected;

    const handleGroupCheckboxChange = (e: React.MouseEvent) => {
      e.stopPropagation(); // Don't toggle expand/collapse
      const newKeys = new Set(selectedRecallKeys);
      if (allSelected) {
        // Deselect all
        groupStopKeys.forEach(key => newKeys.delete(key));
      } else {
        // Select all
        groupStopKeys.forEach(key => newKeys.add(key));
      }
      onSelectionChange(newKeys);
    };

    return (
      <div
        key={groupKey}
        className={cn(
          'rounded-lg border overflow-hidden transition-all',
          groupHighlight === 'blue' && 'border-blue-400 dark:border-blue-600',
          groupHighlight === 'red' && 'border-red-400 dark:border-red-600',
          groupHighlight === 'none' && 'border-accent/30 dark:border-accent/40'
        )}
      >
        <div
          role='button'
          tabIndex={0}
          aria-expanded={isExpanded}
          onClick={() => toggleManualStopGroupExpand(groupKey)}
          onKeyDown={e => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              toggleManualStopGroupExpand(groupKey);
            }
          }}
          className={cn(
            'flex items-center gap-3 px-4 py-3 cursor-pointer transition-colors',
            groupHighlight === 'blue' && 'bg-blue-50/50 dark:bg-blue-950/20',
            groupHighlight === 'red' && 'bg-red-50/50 dark:bg-red-950/20',
            groupHighlight === 'none' &&
              'bg-accent/10 dark:bg-accent/15 hover:bg-accent/15 dark:hover:bg-accent/20'
          )}
        >
          <ChevronRight
            className={cn(
              'h-4 w-4 text-muted-foreground transition-transform duration-200',
              isExpanded && 'rotate-90'
            )}
          />
          {/* Checkbox for group selection */}
          <div
            role='button'
            tabIndex={0}
            className='flex-shrink-0'
            onClick={handleGroupCheckboxChange}
            onKeyDown={e => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                handleGroupCheckboxChange(e as unknown as React.MouseEvent);
              }
            }}
          >
            <Checkbox
              checked={allSelected}
              data-state={someSelected ? 'indeterminate' : allSelected ? 'checked' : 'unchecked'}
              className={cn(
                'transition-all cursor-pointer',
                someSelected && !allSelected && 'data-[state=checked]:bg-primary/50'
              )}
            />
          </div>
          <div className='p-1.5 rounded-full bg-accent/20 text-accent'>
            {group.isCustomer ? (
              <Home className='h-4 w-4' aria-hidden />
            ) : (
              <Building2 className='h-4 w-4' aria-hidden />
            )}
          </div>
          <div className='flex-1 min-w-0'>
            <h4 className='font-semibold text-sm text-foreground truncate'>{group.locationName}</h4>
            {groupAddressLine !== '—' && (
              <div className='flex items-center gap-1 text-xs text-muted-foreground'>
                <MapPin className='h-3 w-3 flex-shrink-0' />
                <span className='truncate'>{groupAddressLine}</span>
              </div>
            )}
          </div>
          <Badge
            className={cn(
              groupHighlight === 'blue' && 'bg-blue-100 text-blue-700 border-blue-300',
              groupHighlight === 'blue' &&
                'dark:bg-blue-900/30 dark:text-blue-400 dark:border-blue-700',
              groupHighlight === 'red' && 'bg-red-100 text-red-700 border-red-300',
              groupHighlight === 'red' &&
                'dark:bg-red-900/30 dark:text-red-400 dark:border-red-700',
              groupHighlight === 'none' &&
                'bg-accent/15 text-accent border border-accent/40 dark:bg-accent/20'
            )}
          >
            {group.stops.length} Job{group.stops.length !== 1 ? 's' : ''}
          </Badge>
        </div>

        {isExpanded && (
          <div className='border-t border-border/50 divide-y divide-border/50'>
            {group.isCustomer && (
              <div className='px-4 py-2'>
                <CustomerContactReveal
                  contactId={group.stops[0]?.contact_id}
                  context='route_details'
                />
              </div>
            )}
            {group.stops.map(stop => renderManualStopRow(stop))}
          </div>
        )}
      </div>
    );
  };

  // Check if all unassigned recalls are selected
  const allUnassignedSelected = useMemo(() => {
    const unassignedKeys = filteredRecalls
      .filter(recall => {
        const key = createRecallKey(recall.vin, recall.campaign_number);
        return !assignmentMap.has(key) && !manualStopKeys.has(key);
      })
      .map(recall => createRecallKey(recall.vin, recall.campaign_number));

    return unassignedKeys.length > 0 && unassignedKeys.every(key => selectedRecallKeys.has(key));
  }, [filteredRecalls, assignmentMap, manualStopKeys, selectedRecallKeys]);

  // Check if all assigned items (recalls + manual stops, ANY technician) are selected
  const allAssignedSelected = useMemo(() => {
    // Assigned recalls
    const assignedRecallKeys = filteredRecalls
      .filter(recall => {
        const key = createRecallKey(recall.vin, recall.campaign_number);
        return assignmentMap.has(key);
      })
      .map(recall => createRecallKey(recall.vin, recall.campaign_number));

    // Manual stops (selected tech)
    const manualStopKeysList = manualStops
      .filter(stop => stop.vin)
      .map(stop => createRecallKey(stop.vin!, stop.campaign_number || ''));

    // Manual stops (other techs)
    const otherTechManualStopKeys: string[] = [];
    manualStopsByOtherTech.forEach(stops => {
      stops.forEach(stop => {
        if (stop.vin) {
          otherTechManualStopKeys.push(createRecallKey(stop.vin, stop.campaign_number || ''));
        }
      });
    });

    const allAssignedKeys = [
      ...assignedRecallKeys,
      ...manualStopKeysList,
      ...otherTechManualStopKeys
    ];

    return allAssignedKeys.length > 0 && allAssignedKeys.every(key => selectedRecallKeys.has(key));
  }, [filteredRecalls, assignmentMap, manualStops, manualStopsByOtherTech, selectedRecallKeys]);

  // Check if there are any assigned items (for checkbox disabled state)
  const hasAnyAssigned = useMemo(() => {
    const hasAssignedRecalls = filteredRecalls.some(recall => {
      const key = createRecallKey(recall.vin, recall.campaign_number);
      return assignmentMap.has(key);
    });
    const hasManualStops = manualStops.length > 0;
    const hasOtherTechManualStops = manualStopsByOtherTech.size > 0;

    return hasAssignedRecalls || hasManualStops || hasOtherTechManualStops;
  }, [filteredRecalls, assignmentMap, manualStops, manualStopsByOtherTech]);

  // Check if there are any unassigned recalls (for checkbox disabled state)
  const hasAnyUnassigned = useMemo(() => {
    return filteredRecalls.some(recall => {
      const key = createRecallKey(recall.vin, recall.campaign_number);
      return !assignmentMap.has(key) && !manualStopKeys.has(key);
    });
  }, [filteredRecalls, assignmentMap, manualStopKeys]);

  // Compute button label based on assign/reassign counts
  const assignButtonLabel = useMemo(() => {
    const parts: string[] = [];
    if (selectedNewAssignCount > 0) {
      parts.push(`Assign (${selectedNewAssignCount})`);
    }
    if (selectedReassignCount > 0) {
      parts.push(`Reassign (${selectedReassignCount})`);
    }
    if (startLocationDirty && parts.length > 0) {
      parts.push('Update Start');
    }
    if (parts.length > 0) return parts.join(' + ');
    if (startLocationDirty) return 'Update Start Location';
    return 'Assign';
  }, [selectedNewAssignCount, selectedReassignCount, startLocationDirty]);

  // Compute tooltip text for the assign button
  const assignButtonTooltip = useMemo(() => {
    if (!technicianName) return 'Select a technician first';
    if (selectedAssignableCount === 0 && !startLocationDirty) {
      return selectedRecallKeys.size > 0
        ? 'Selected recalls are already assigned to this technician on this date'
        : 'Select recalls to assign';
    }
    if (selectedAssignableCount === 0 && startLocationDirty) {
      return `Update route start location for ${technicianName}`;
    }
    const parts: string[] = [];
    if (selectedNewAssignCount > 0) {
      parts.push(
        `Assign ${selectedNewAssignCount} recall${selectedNewAssignCount !== 1 ? 's' : ''}`
      );
    }
    if (selectedReassignCount > 0) {
      parts.push(
        `Reassign ${selectedReassignCount} recall${selectedReassignCount !== 1 ? 's' : ''}`
      );
    }
    if (startLocationDirty) {
      parts.push('update start location');
    }
    return parts.join(', ') + ` for ${technicianName}`;
  }, [
    technicianName,
    selectedAssignableCount,
    startLocationDirty,
    selectedRecallKeys.size,
    selectedNewAssignCount,
    selectedReassignCount
  ]);

  // Render a single dealer group
  // sectionKey is used to create unique keys when the same dealer appears in multiple sections
  const renderDealerGroup = (group: DealerGroup, sectionKey: string = 'default') => {
    const uniqueKey = `${sectionKey}-${group.groupKey}`;
    const isExpanded = expandedDealers.has(uniqueKey);

    // Calculate selection stats for this group
    const groupSelectedCount = group.recalls.filter(recall =>
      selectedRecallKeys.has(createRecallKey(recall.vin, recall.campaign_number))
    ).length;

    // Calculate all selected - consider recalls that can be selected (unassigned or reassignment allowed)
    const selectableRecalls = group.recalls.filter(recall => {
      const key = createRecallKey(recall.vin, recall.campaign_number);
      return !assignmentMap.has(key) || allowReassignment;
    });

    const groupAllSelected =
      selectableRecalls.length > 0 &&
      selectableRecalls.every(recall =>
        selectedRecallKeys.has(createRecallKey(recall.vin, recall.campaign_number))
      );
    const groupSomeSelected = groupSelectedCount > 0 && !groupAllSelected;

    // Check recalls in THIS specific section's group (not all dealer recalls across sections)
    // This prevents the same dealer appearing in multiple sections from all being highlighted
    const hasAssignedRecalls = group.recalls.some(recall =>
      assignmentMap.has(createRecallKey(recall.vin, recall.campaign_number))
    );

    // Determine the "worst" highlight state across all recalls in this dealer group
    // Priority: red (overdue) > blue (today) > none
    const groupHighlightState: HighlightState = (() => {
      let hasBlue = false;
      for (const recall of group.recalls) {
        const key = createRecallKey(recall.vin, recall.campaign_number);
        const state = getHighlightState(assignmentMap.get(key));
        if (state === 'red') return 'red';
        if (state === 'blue') hasBlue = true;
      }
      return hasBlue ? 'blue' : 'none';
    })();

    return (
      <div key={uniqueKey} className='space-y-2'>
        <DealerGroupHeader
          dealerName={group.dealerName}
          isCustomer={!!group.isCustomer}
          {...(group.dealerAddress !== undefined && { dealerAddress: group.dealerAddress })}
          {...(group.dealerCity !== undefined && { dealerCity: group.dealerCity })}
          {...(group.dealerRegion !== undefined && { dealerRegion: group.dealerRegion })}
          {...(group.dealerPostalCode !== undefined && {
            dealerPostalCode: group.dealerPostalCode
          })}
          recallCount={group.recalls.length}
          selectedCount={groupSelectedCount}
          isExpanded={isExpanded}
          onToggleExpand={() => toggleDealerExpand(uniqueKey)}
          onSelectAll={selected => handleDealerSelectAll(group.recalls, selected)}
          allSelected={groupAllSelected}
          someSelected={groupSomeSelected}
          hasAssignedRecalls={hasAssignedRecalls}
          groupHighlightState={groupHighlightState}
          dealerBrand={group.recalls.every(r => r.is_manual_stop) ? 'manual' : 'vehix'}
        />

        {isExpanded && (
          <div className='ml-6 space-y-3'>
            {/* One contact reveal per household, not one per vehicle card */}
            {group.isCustomer && (
              <CustomerContactReveal
                contactId={group.recalls[0]?.contact_id}
                context='route_details'
              />
            )}
            {group.recalls.map(recall => {
              const key = createRecallKey(recall.vin, recall.campaign_number);
              const assignment = assignmentMap.get(key);
              const isAssigned = !!assignment;
              const highlightState = getHighlightState(assignment);
              const isSelected = selectedRecallKeys.has(key);

              // Format scheduled date for display (date only, no time)
              // IMPORTANT: Extract the date part and parse as local date to avoid UTC timezone shift
              // Database returns TIMESTAMPTZ like "2026-01-27T00:00:00+00:00" but we only care about the date
              let scheduledDate: string | undefined;
              if (assignment?.expected_visit_date) {
                try {
                  const dateStr = assignment.expected_visit_date;

                  // Extract YYYY-MM-DD from the string (handles both date-only and timestamps)
                  // "2026-01-27" → "2026-01-27"
                  // "2026-01-27T00:00:00+00:00" → "2026-01-27"
                  // "2026-01-27T00:00:00.000Z" → "2026-01-27"
                  const dateMatch =
                    typeof dateStr === 'string' && dateStr.match(/^(\d{4}-\d{2}-\d{2})/);

                  let date: Date;
                  if (dateMatch && dateMatch[1]) {
                    // Parse the extracted date as local date to avoid UTC shift
                    const parts = dateMatch[1].split('-').map(Number);
                    const year = parts[0] ?? 2026;
                    const month = parts[1] ?? 1;
                    const day = parts[2] ?? 1;
                    date = new Date(year, month - 1, day, 12, 0, 0);
                  } else {
                    // Fallback for unexpected formats
                    date = new Date(dateStr);
                  }

                  scheduledDate = date.toLocaleDateString('en-US', {
                    month: 'short',
                    day: 'numeric',
                    year: 'numeric'
                  });
                } catch {
                  scheduledDate = undefined;
                }
              }

              return (
                <RecallAssignmentCard
                  key={key}
                  recall={{
                    ...recall,
                    ...(assignment?.technician_name && { assignedTo: assignment.technician_name }),
                    ...(assignment?.id && { assignmentId: assignment.id }),
                    ...(scheduledDate && { scheduledDate }),
                    ...(assignment?.assigned_by_name && {
                      assignedByName: assignment.assigned_by_name
                    })
                  }}
                  isSelected={isSelected}
                  isAssigned={isAssigned}
                  onSelect={handleRecallSelect}
                  {...(onUnassign && { onUnassign })}
                  {...(onRemoveRecall && { onRemove: onRemoveRecall })}
                  allowReassignment={allowReassignment}
                  highlightState={highlightState}
                />
              );
            })}
          </div>
        )}
      </div>
    );
  };

  // Loading state
  if (loading) {
    return (
      <div className={cn('flex flex-col h-full bg-card rounded-lg border', className)}>
        <div className='flex items-center justify-center h-64'>
          <div className='animate-pulse text-muted-foreground'>Loading recalls...</div>
        </div>
      </div>
    );
  }

  return (
    <div className={cn('flex flex-col h-full bg-card rounded-lg border', className)}>
      {/* Header with Tabs, Search, Actions and Selection */}
      <div className='p-3 border-b space-y-2'>
        {/* Row 1: Stop Type Filter + Search */}
        <div className='flex items-center gap-3'>
          {/* Stop Type Filter Buttons */}
          <div className='flex items-center rounded-md text-sm shrink-0 overflow-hidden border'>
            <button
              type='button'
              onClick={() => setStopTypeFilter('all_stops')}
              className={cn(
                'px-2.5 py-1.5 transition-colors',
                stopTypeFilter === 'all_stops'
                  ? 'bg-foreground text-background font-medium'
                  : 'bg-muted/50 text-muted-foreground hover:bg-muted'
              )}
            >
              All: <strong>{recallCount + stagedManualStopCount + manualStops.length}</strong>
            </button>
            <button
              type='button'
              onClick={() => setStopTypeFilter('recalls')}
              className={cn(
                'px-2.5 py-1.5 border-l transition-colors',
                stopTypeFilter === 'recalls'
                  ? 'bg-primary text-primary-foreground font-medium'
                  : 'bg-muted/50 text-muted-foreground hover:bg-muted'
              )}
            >
              Recalls: <strong>{recallCount}</strong>
            </button>
            <button
              type='button'
              onClick={() => setStopTypeFilter('manual')}
              className={cn(
                'px-2.5 py-1.5 border-l transition-colors flex items-center gap-1',
                stopTypeFilter === 'manual'
                  ? 'bg-accent text-accent-foreground font-medium'
                  : 'bg-muted/50 text-muted-foreground hover:bg-muted'
              )}
            >
              <Wrench className='h-3 w-3' />
              Manual: <strong>{stagedManualStopCount + manualStops.length}</strong>
            </button>
          </div>

          {/* Search - takes remaining space */}
          <div className='relative flex-1'>
            <Search className='absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground' />
            <Input
              type='text'
              placeholder='Search by VIN, campaign, dealer, or vehicle...'
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              className='pl-9 bg-background h-9'
            />
          </div>
        </div>

        {/* Row 2: Actions fill space, checkboxes on right */}
        <div className='flex items-center gap-4 flex-wrap'>
          {/* Action buttons - flex-1 to fill space */}
          <div className='flex items-center gap-1.5 flex-1'>
            {/* Assign Button */}
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    size='default'
                    onClick={onBulkAssign}
                    disabled={
                      !onBulkAssign ||
                      (selectedAssignableCount === 0 && !startLocationDirty) ||
                      !technicianName ||
                      isAssigning
                    }
                    className='h-9 text-sm font-semibold flex-1 max-w-xs'
                  >
                    {isAssigning ? (
                      <>
                        <span className='animate-spin mr-1.5'>⏳</span>
                        Assigning...
                      </>
                    ) : (
                      <>
                        <UserPlus className='h-4 w-4 mr-1.5' />
                        {assignButtonLabel}
                      </>
                    )}
                  </Button>
                </TooltipTrigger>
                <TooltipContent>{assignButtonTooltip}</TooltipContent>
              </Tooltip>
            </TooltipProvider>

            {/* Unassign Selected Button */}
            {selectedAssignedToTechCount > 0 && onBulkUnassign && (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      size='sm'
                      variant='outline'
                      onClick={() => onBulkUnassign(getSelectedAssignmentIds())}
                      disabled={isAssigning}
                      className={cn(
                        'h-9 px-3 text-primary border-primary/40 hover:bg-primary/10',
                        'dark:hover:bg-primary/15'
                      )}
                    >
                      <UserPlus className='h-4 w-4 mr-1.5 rotate-180' />
                      Unassign ({selectedAssignedToTechCount})
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    {`Unassign ${selectedAssignedToTechCount} selected ` +
                      `recall${selectedAssignedToTechCount !== 1 ? 's' : ''} from ${technicianName}`}
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}

            {/* Clear Selected Unassigned Button */}
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    size='sm'
                    variant='outline'
                    onClick={() => setShowClearUnassignedDialog(true)}
                    disabled={!onClearSelectedUnassigned || selectedUnassignedCount === 0}
                    className={cn(
                      'h-9 px-3 text-red-700 border-red-300 hover:bg-red-50 hover:text-red-900',
                      'dark:text-red-400 dark:border-red-700 dark:hover:bg-red-900/30'
                    )}
                  >
                    <Trash2 className='h-4 w-4 mr-1.5' />
                    Clear ({selectedUnassignedCount})
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  {selectedUnassignedCount === 0
                    ? 'Select unassigned recalls to clear'
                    : `Remove ${selectedUnassignedCount} selected unassigned recalls from route`}
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>

          {/* Right: Selection checkboxes */}
          <div className='flex items-center gap-3'>
            <div
              role='button'
              tabIndex={0}
              onClick={() => hasAnyAssigned && handleSelectAllAssigned()}
              onKeyDown={e => {
                if ((e.key === 'Enter' || e.key === ' ') && hasAnyAssigned) {
                  e.preventDefault();
                  handleSelectAllAssigned();
                }
              }}
              className={cn(
                'flex items-center gap-1.5 cursor-pointer text-xs',
                'text-muted-foreground hover:text-foreground',
                !hasAnyAssigned && 'opacity-50 cursor-not-allowed'
              )}
            >
              <Checkbox
                checked={allAssignedSelected}
                onCheckedChange={() => handleSelectAllAssigned()}
                disabled={!hasAnyAssigned}
              />
              <span className='hidden sm:inline'>All Assigned</span>
              <span className='sm:hidden'>Asgn</span>
            </div>

            <div
              role='button'
              tabIndex={0}
              onClick={() => hasAnyUnassigned && handleSelectAllUnassigned()}
              onKeyDown={e => {
                if ((e.key === 'Enter' || e.key === ' ') && hasAnyUnassigned) {
                  e.preventDefault();
                  handleSelectAllUnassigned();
                }
              }}
              className={cn(
                'flex items-center gap-1.5 cursor-pointer text-xs',
                'text-muted-foreground hover:text-foreground',
                !hasAnyUnassigned && 'opacity-50 cursor-not-allowed'
              )}
            >
              <Checkbox
                checked={allUnassignedSelected}
                onCheckedChange={() => handleSelectAllUnassigned()}
                disabled={!hasAnyUnassigned}
              />
              <span className='hidden sm:inline'>All Unassigned</span>
              <span className='sm:hidden'>Unasgn</span>
            </div>
          </div>
        </div>
      </div>

      {/* Content Area */}
      {/* Force Radix's viewport wrapper to display:block so it respects the panel
          width instead of growing to content (display:table). Without this, long
          recall descriptions widen the row past the panel and the right-side
          assignment info is clipped by the panel's overflow-hidden. */}
      <ScrollArea className='flex-1 [&>[data-radix-scroll-area-viewport]>div]:!block'>
        <div className='p-4 space-y-4'>
          {/* Empty state */}
          {dealerGroups.length === 0 && manualStopGroups.length === 0 ? (
            <div className='flex flex-col items-center justify-center py-12 text-center'>
              <Inbox className='h-12 w-12 text-muted-foreground/50 mb-4' />
              <h3 className='text-lg font-medium text-foreground mb-1'>
                {searchTerm ? 'No matching recalls' : 'No stops to display'}
              </h3>
              <p className='text-sm text-muted-foreground'>
                {searchTerm
                  ? 'Try adjusting your search terms'
                  : filterMode === 'assigned'
                    ? 'No recalls assigned to this technician'
                    : filterMode === 'unassigned'
                      ? 'All recalls have been assigned'
                      : 'Select recalls from the Recall Map to begin routing'}
              </p>
            </div>
          ) : filterMode === 'all' ? (
            <>
              {/* Assigned to selected technician section */}
              {technicianName &&
                (manualStopGroups.length > 0 || assignedToSelectedGroups.length > 0) && (
                  <div className='flex items-center gap-3 py-2'>
                    <div className='flex-1 border-t border-primary/30' />
                    <span className='text-xs font-medium text-primary uppercase tracking-wide'>
                      Assigned to {technicianName}
                    </span>
                    <div className='flex-1 border-t border-primary/30' />
                  </div>
                )}

              {/* Manual Stops Section - shown under tech header */}
              {manualStopGroups.length > 0 && (
                <>
                  <div className='flex items-center gap-3 py-1 ml-4'>
                    <span
                      className={cn(
                        'text-xs font-medium text-accent uppercase tracking-wide',
                        'flex items-center gap-1.5'
                      )}
                    >
                      <Wrench className='h-3.5 w-3.5' />
                      Manual Stops ({manualStops.length})
                    </span>
                    <div className='flex-1 border-t border-accent/30' />
                  </div>
                  {manualStopGroups.map(group =>
                    renderManualStopLocationGroup(group, `manual-${group.key}`)
                  )}
                </>
              )}

              {/* Section 1: Recalls assigned to selected technician */}
              {assignedToSelectedGroups.length > 0 && technicianName && (
                <div className='flex items-center gap-3 py-1 ml-4'>
                  <span className='text-xs font-medium text-primary uppercase tracking-wide'>
                    Vehix Recalls (
                    {assignedToSelectedGroups.reduce((sum, g) => sum + g.recalls.length, 0)})
                  </span>
                  <div className='flex-1 border-t border-primary/30' />
                </div>
              )}
              {assignedToSelectedGroups.map(group => renderDealerGroup(group, 'selected'))}

              {/* Section 2: Assigned to other technicians (recalls + manual stops) */}
              {(assignedToOthersMap.size > 0 || manualStopGroupsByOtherTech.size > 0) &&
                // Merge tech names from both recalls and manual stops
                Array.from(
                  new Set([...assignedToOthersMap.keys(), ...manualStopGroupsByOtherTech.keys()])
                )
                  .sort((a, b) => a.localeCompare(b))
                  .map(otherTechName => {
                    const recallGroups = assignedToOthersMap.get(otherTechName) || [];
                    const manualGroups = manualStopGroupsByOtherTech.get(otherTechName) || [];
                    const totalManualStops = manualGroups.reduce(
                      (sum, g) => sum + g.stops.length,
                      0
                    );
                    const totalRecalls = recallGroups.reduce((sum, g) => sum + g.recalls.length, 0);

                    return (
                      <React.Fragment key={`other-tech-${otherTechName}`}>
                        <div className='flex items-center gap-3 py-2'>
                          <div className='flex-1 border-t border-border' />
                          <span className='text-xs font-medium text-muted-foreground'>
                            ASSIGNED TO {otherTechName.toUpperCase()}
                          </span>
                          <div className='flex-1 border-t border-border' />
                        </div>

                        {/* Manual stops for this tech */}
                        {manualGroups.length > 0 && (
                          <>
                            <div className='flex items-center gap-3 py-1 ml-4'>
                              <span
                                className={cn(
                                  'text-xs font-medium text-accent uppercase tracking-wide',
                                  'flex items-center gap-1.5'
                                )}
                              >
                                <Wrench className='h-3.5 w-3.5' />
                                Manual Stops ({totalManualStops})
                              </span>
                              <div className='flex-1 border-t border-accent/30' />
                            </div>
                            {manualGroups.map(group =>
                              renderManualStopLocationGroup(
                                group,
                                `manual-other-${otherTechName}-${group.key}`
                              )
                            )}
                          </>
                        )}

                        {/* Recalls for this tech */}
                        {recallGroups.length > 0 && manualGroups.length > 0 && (
                          <div className='flex items-center gap-3 py-1 ml-4'>
                            <span className='text-xs font-medium text-primary uppercase tracking-wide'>
                              Vehix Recalls ({totalRecalls})
                            </span>
                            <div className='flex-1 border-t border-primary/30' />
                          </div>
                        )}
                        {recallGroups.map(group =>
                          renderDealerGroup(group, `other-${otherTechName}`)
                        )}
                      </React.Fragment>
                    );
                  })}

              {/* Section 3: Truly unassigned */}
              {trulyUnassignedGroups.length > 0 && (
                <div className='flex items-center gap-3 py-2'>
                  <div className='flex-1 border-t border-border' />
                  <span className='text-xs font-medium text-muted-foreground uppercase tracking-wide'>
                    Unassigned
                  </span>
                  <div className='flex-1 border-t border-border' />
                </div>
              )}
              {trulyUnassignedGroups.map(group => renderDealerGroup(group, 'unassigned'))}
            </>
          ) : (
            /* Non-'all' modes: just render dealerGroups once, plus manual stops for All Stops view */
            <>
              {/* Manual Stops Section - shown at top when in assigned filter */}
              {filterMode === 'assigned' && manualStopGroups.length > 0 && (
                <>
                  <div className='flex items-center gap-3 py-2'>
                    <div className='flex-1 border-t border-accent/30' />
                    <span
                      className={cn(
                        'text-xs font-medium text-accent uppercase tracking-wide',
                        'flex items-center gap-1.5'
                      )}
                    >
                      <Wrench className='h-3.5 w-3.5' />
                      Manual Stops ({manualStops.length})
                    </span>
                    <div className='flex-1 border-t border-accent/30' />
                  </div>
                  {manualStopGroups.map(group =>
                    renderManualStopLocationGroup(group, `manual-assigned-${group.key}`)
                  )}
                </>
              )}
              {/* Recalls section header when showing assigned filter with manual stops */}
              {filterMode === 'assigned' &&
                manualStopGroups.length > 0 &&
                dealerGroups.length > 0 && (
                  <div className='flex items-center gap-3 py-2'>
                    <div className='flex-1 border-t border-primary/30' />
                    <span className='text-xs font-medium text-primary uppercase tracking-wide'>
                      Vehix Recalls ({dealerGroups.reduce((sum, g) => sum + g.recalls.length, 0)})
                    </span>
                    <div className='flex-1 border-t border-primary/30' />
                  </div>
                )}
              {dealerGroups.map(group => renderDealerGroup(group, filterMode))}
            </>
          )}
        </div>
      </ScrollArea>

      {/* Clear All Confirmation Dialog */}
      <Dialog open={showClearConfirmDialog} onOpenChange={setShowClearConfirmDialog}>
        <DialogContent className='sm:max-w-[425px]'>
          <DialogHeader>
            <div className='flex items-center gap-3'>
              <div className='flex h-10 w-10 items-center justify-center rounded-full bg-destructive/10'>
                <AlertTriangle className='h-5 w-5 text-destructive' />
              </div>
              <DialogTitle>Clear All Recalls from {technicianName}?</DialogTitle>
            </div>
            <DialogDescription className='pt-2'>
              This will unassign all {techAssignedCount} recall{techAssignedCount !== 1 ? 's' : ''}{' '}
              from {technicianName}. The recalls will remain in your route list but will no longer
              be assigned to this technician.
              <br />
              <br />
              <strong className='text-foreground'>This action cannot be undone.</strong>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              type='button'
              variant='outline'
              onClick={() => setShowClearConfirmDialog(false)}
            >
              Cancel
            </Button>
            <Button
              type='button'
              variant='destructive'
              onClick={() => {
                onClearAllFromTech?.();
                setShowClearConfirmDialog(false);
              }}
            >
              Clear All Recalls
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Clear Selected Unassigned Confirmation Dialog */}
      <Dialog open={showClearUnassignedDialog} onOpenChange={setShowClearUnassignedDialog}>
        <DialogContent className='sm:max-w-[425px]'>
          <DialogHeader>
            <div className='flex items-center gap-3'>
              <div className='flex h-10 w-10 items-center justify-center rounded-full bg-destructive/10'>
                <AlertTriangle className='h-5 w-5 text-destructive' />
              </div>
              <DialogTitle>Clear Unassigned Recalls?</DialogTitle>
            </div>
            <DialogDescription className='pt-2'>
              This will remove {selectedUnassignedCount} recall
              {selectedUnassignedCount !== 1 ? 's' : ''} from the assignment list.
              {selectedUnassignedCount !== 1 ? ' They' : ' It'} will need to be re-selected from the
              recall map or list if you want to work on{' '}
              {selectedUnassignedCount !== 1 ? 'them' : 'it'} again.
              <br />
              <br />
              <strong className='text-foreground'>This action cannot be undone.</strong>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              type='button'
              variant='outline'
              onClick={() => setShowClearUnassignedDialog(false)}
            >
              Cancel
            </Button>
            <Button
              type='button'
              variant='destructive'
              onClick={() => {
                onClearSelectedUnassigned?.();
                setShowClearUnassignedDialog(false);
              }}
            >
              Clear Unassigned
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default RecallAssignmentList;
